import React from 'react';
import Sparkline from './Sparkline';
import type { SparklinePoint } from '../../types/dashboard';

interface KpiItem {
  label: string;
  value: number | string;
  delta?: number | null;
}

interface KpiCardProps {
  title: string;
  items: KpiItem[];
  sparkline?: SparklinePoint[];
  sparklineColor?: string;
  subtitle?: string;
}

const cardStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 240,
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: '20px 24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 8,
};

const itemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const valueStyle: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 700,
  color: '#111827',
  lineHeight: 1,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#9ca3af',
  fontWeight: 500,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  margin: 0,
};

function DeltaBadge({ delta }: { delta: number }) {
  const isUp = delta >= 0;
  const color = isUp ? '#059669' : '#dc2626';
  const bg = isUp ? '#ecfdf5' : '#fef2f2';
  const arrow = isUp ? '▲' : '▼';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 11,
        fontWeight: 600,
        color,
        backgroundColor: bg,
        borderRadius: 4,
        padding: '2px 6px',
      }}
    >
      {arrow} {Math.abs(delta)}
    </span>
  );
}

export default function KpiCard({ title, items, sparkline, sparklineColor, subtitle }: KpiCardProps) {
  return (
    <div style={cardStyle}>
      <p style={titleStyle}>{title}</p>
      <div style={rowStyle}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {items.map((item) => (
            <div key={item.label} style={itemStyle}>
              <span style={valueStyle}>{item.value}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={labelStyle}>{item.label}</span>
                {item.delta != null && item.delta !== 0 && <DeltaBadge delta={item.delta} />}
              </div>
            </div>
          ))}
        </div>
        {sparkline && sparkline.length > 0 && (
          <Sparkline data={sparkline} color={sparklineColor} />
        )}
      </div>
      {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
    </div>
  );
}
